// Editor de personagem: uma janela (dialog) com a prévia grande e a escolha por peças (corpo,
// pele, cabelo, barba, olhos, parte de cima, parte de baixo, sapatos e acessório).
//
// Usado pelo painel do escritório (lápis, só admin) e pelo CMS ("Editar peças"). Não grava
// sozinho: quem abre passa `salvar`, que devolve a mensagem de erro (ou undefined, se deu certo).
// A lógica (sorteio, nome único, validação) fica em src/core/look-edit.ts.

import { ACESSORIOS, BARBA, comAcessorio, NOME_MAX, nomeProblema, OLHOS, sorteiaLook } from '../core/look-edit.js';
import { BODY_IDS, HAIR_IDS, OUTFIT_IDS, type AccessoryId, type Look } from '../core/look.js';
import type { PersonaSettings } from '../core/settings.js';
import { ACCESSORY_BY_ID, BODY_BY_ID, HAIR_BY_ID, OUTFIT_BY_ID } from './art/character/index.js';
import { CHAR_H, CHAR_W } from './art/types.js';
import { CLOTH_COLORS, HAIR_COLORS, type Ramp, SKIN_TONES } from './art/palette.js';
import { characterFrame } from './engine/character.js';
import { t } from './i18n.js';
// estilo próprio: o mesmo bloco serve o escritório (app.css) e o CMS (cms.css)
import './editor.css';

export interface OpcoesEditor {
  /** Título da janela (já traduzido). */
  titulo: string;
  /** O que o escritório mostra agora: ponto de partida do editor. */
  atual: { look: Look; nome: string };
  /** O sorteio automático, sem escolha salva: é para onde vai "Voltar ao sorteio". */
  sorteio: { look: Look; nome: string };
  /** Nomes que o personagem não pode repetir (ver nomesEmUso em src/core/look-edit.ts). Lido a cada conferência: o escritório muda enquanto a janela está aberta. */
  nomesEmUso: () => readonly string[];
  /** Personagem do elenco guardado no ajuste: é mantido ao salvar. */
  castId?: string;
  /** Grava o personagem. Devolve a mensagem de erro, ou undefined se deu certo. */
  salvar: (persona: PersonaSettings) => Promise<string | undefined>;
}

type Attrs = Record<string, string>;
type Filho = Node | string | null | undefined | false;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs = {}, ...filhos: Filho[]): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [chave, valor] of Object.entries(attrs)) node.setAttribute(chave, valor);
  node.append(...filhos.filter((f): f is Node | string => f !== null && f !== undefined && f !== false));
  return node;
}

/** Desenha o personagem de frente, parado, no tamanho de arte (16×24). */
function desenhaPersonagem(canvas: HTMLCanvasElement, look: Look): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(characterFrame(look, 'down', 'idle'), 0, 0);
}

/** Canvas do personagem com o tamanho na tela (inline: a janela também abre no CMS). */
function telaDoPersonagem(look: Look, px: { w: number; h: number }, rotulo?: string): HTMLCanvasElement {
  const canvas = el('canvas', {
    width: String(CHAR_W),
    height: String(CHAR_H),
    style: `width:${px.w}px;height:${px.h}px;image-rendering:pixelated`,
    ...(rotulo ? { role: 'img', 'aria-label': rotulo } : { 'aria-hidden': 'true' }),
  });
  desenhaPersonagem(canvas, look);
  return canvas;
}

/** Uma opção com miniatura do personagem inteiro e o nome da peça embaixo. */
function opcao(rotulo: string, look: Look, marcado: boolean, foco: string, aplica: () => void): HTMLButtonElement {
  const botao = el('button', { type: 'button', class: 'ed-opcao', 'aria-pressed': String(marcado), 'data-foco': foco }, telaDoPersonagem(look, { w: 32, h: 48 }), el('span', { class: 'ed-rotulo' }, rotulo));
  botao.addEventListener('click', aplica);
  return botao;
}

/** Amostra de cor (um quadradinho da rampa). */
function amostra(ramp: Ramp, n: number, marcado: boolean, foco: string, aplica: () => void): HTMLButtonElement {
  const botao = el('button', { type: 'button', class: 'ed-cor', 'aria-pressed': String(marcado), 'aria-label': t('ed.cor', { n: n + 1 }), title: t('ed.cor', { n: n + 1 }), 'data-foco': foco, style: `background:${ramp[2]}` });
  botao.addEventListener('click', aplica);
  return botao;
}

/** Grupo com legenda (fieldset: leitor de tela anuncia o nome da peça). */
function grupo(titulo: string, ...filhos: Filho[]): HTMLFieldSetElement {
  return el('fieldset', { class: 'ed-grupo' }, el('legend', { class: 'ed-legenda' }, titulo), ...filhos);
}

/** Abre o editor numa janela modal. Fecha com Esc, com Cancelar ou depois de salvar. */
export function abreEditorPersonagem(opcoes: OpcoesEditor): void {
  let look: Look = { ...opcoes.atual.look };
  let ocupado = false;

  const previa = telaDoPersonagem(look, { w: 128, h: 192 }, t('ed.previa'));
  const campoNome = el('input', { type: 'text', id: 'ed-nome', maxlength: String(NOME_MAX), autocomplete: 'off', 'aria-describedby': 'ed-nome-dica ed-nome-erro' });
  campoNome.value = opcoes.atual.nome;
  const dicaNome = el('p', { id: 'ed-nome-dica', class: 'ed-dica' }, t('ed.nome_dica', { max: NOME_MAX }));
  const erroNome = el('p', { id: 'ed-nome-erro', class: 'ed-erro-campo', role: 'status' });
  const erroGeral = el('p', { class: 'ed-erro', role: 'alert' });
  const controles = el('div', { class: 'ed-controles' });
  const botaoSalvar = el('button', { type: 'submit', class: 'primario' }, t('ed.salvar'));
  const dlg = el('dialog', { class: 'editor-personagem', 'aria-labelledby': 'editor-personagem-titulo' });

  /** Mostra o problema do nome (ou apaga a mensagem). */
  function mostraNome(): string | undefined {
    const problema = nomeProblema(campoNome.value, opcoes.nomesEmUso());
    const texto = problema === 'vazio' ? t('ed.nome_vazio') : problema === 'longo' ? t('ed.nome_longo', { max: NOME_MAX }) : problema === 'repetido' ? t('ed.nome_repetido') : '';
    erroNome.textContent = texto;
    campoNome.setAttribute('aria-invalid', String(Boolean(texto)));
    return problema;
  }

  /** Troca uma peça (ou várias) e repinta a prévia e os controles. */
  function ajusta(f: (atual: Look) => Look): void {
    look = f(look);
    atualiza();
  }

  /** Repinta a prévia e os grupos. O foco volta ao mesmo botão (quem usa teclado não se perde). */
  function atualiza(): void {
    const focado = dlg.contains(document.activeElement) ? (document.activeElement as HTMLElement).dataset.foco : undefined;
    desenhaPersonagem(previa, look);
    controles.replaceChildren(...grupos());
    if (focado) controles.querySelector<HTMLElement>(`[data-foco="${CSS.escape(focado)}"]`)?.focus();
  }

  /** Opções de um grupo de estilo (corpo, cabelo, roupa): uma miniatura por id. */
  function estilos<T extends string>(titulo: string, ids: readonly T[], nomeDe: (id: T) => string, campo: 'body' | 'hair' | 'outfit'): HTMLFieldSetElement {
    const com = (l: Look, id: T): Look => ({ ...l, [campo]: id }) as Look;
    return grupo(
      titulo,
      el('div', { class: 'ed-escolhas' }, ...ids.map((id) => opcao(nomeDe(id), com(look, id), look[campo] === id, `${campo}:${id}`, () => ajusta((l) => com(l, id))))),
    );
  }

  /** Amostras de uma rampa de cores para um campo do Look. */
  function cores(titulo: string, paleta: readonly Ramp[], atual: number, campo: 'skin' | 'hairColor' | 'shirt' | 'pants' | 'shoes' | 'accColor'): HTMLFieldSetElement {
    const com = (l: Look, n: number): Look => ({ ...l, [campo]: n }) as Look;
    return grupo(
      titulo,
      el('div', { class: 'ed-cores' }, ...paleta.map((ramp, n) => amostra(ramp, n, atual === n, `${campo}:${n}`, () => ajusta((l) => com(l, n))))),
    );
  }

  /** Grupo de um slot de acessório: "Nenhum" limpa só se o acessório atual for deste grupo. */
  function grupoDeAcessorio(titulo: string, ids: readonly AccessoryId[]): HTMLFieldSetElement {
    const dono = look.accessory !== undefined && ids.includes(look.accessory);
    const nenhum = opcao(t('ed.nenhum'), comAcessorio(look, undefined), !dono, `acc-${titulo}:none`, () => {
      if (dono) ajusta((l) => comAcessorio(l, undefined));
    });
    return grupo(
      titulo,
      el(
        'div',
        { class: 'ed-escolhas' },
        nenhum,
        ...ids.map((id) => opcao(ACCESSORY_BY_ID.get(id)?.name ?? id, comAcessorio(look, id), look.accessory === id, `acc:${id}`, () => ajusta((l) => comAcessorio(l, id)))),
      ),
    );
  }

  /** Todos os grupos, na ordem do editor. */
  function grupos(): HTMLElement[] {
    const lista: HTMLElement[] = [
      estilos(t('ed.grupo_corpo'), BODY_IDS, (id) => BODY_BY_ID.get(id)?.name ?? id, 'body'),
      cores(t('ed.grupo_pele'), SKIN_TONES, look.skin, 'skin'),
      estilos(t('ed.grupo_cabelo'), HAIR_IDS, (id) => HAIR_BY_ID.get(id)?.name ?? id, 'hair'),
      cores(t('ed.grupo_cabelo_cor'), HAIR_COLORS, look.hairColor, 'hairColor'),
      grupoDeAcessorio(t('ed.grupo_barba'), BARBA),
      grupoDeAcessorio(t('ed.grupo_olhos'), OLHOS),
      grupoDeAcessorio(t('ed.grupo_acessorio'), ACESSORIOS),
    ];
    if (look.accessory !== undefined) lista.push(cores(t('ed.grupo_acessorio_cor'), CLOTH_COLORS, look.accColor ?? 0, 'accColor'));
    lista.push(
      estilos(t('ed.grupo_cima'), OUTFIT_IDS, (id) => OUTFIT_BY_ID.get(id)?.name ?? id, 'outfit'),
      cores(t('ed.grupo_cima_cor'), CLOTH_COLORS, look.shirt, 'shirt'),
      cores(t('ed.grupo_baixo'), CLOTH_COLORS, look.pants, 'pants'),
      cores(t('ed.grupo_sapatos'), CLOTH_COLORS, look.shoes, 'shoes'),
    );
    return lista;
  }

  const botaoFechar = el('button', { type: 'button', class: 'fechar', 'aria-label': t('ed.fechar') }, '✕');
  const botaoSortear = el('button', { type: 'button', class: 'secundario' }, t('ed.sortear'));
  const botaoVoltar = el('button', { type: 'button', class: 'secundario' }, t('ed.voltar_sorteio'));
  const botaoCancelar = el('button', { type: 'button', class: 'secundario' }, t('ed.cancelar'));

  const fecha = (): void => dlg.close();
  botaoFechar.addEventListener('click', fecha);
  botaoCancelar.addEventListener('click', fecha);
  botaoSortear.addEventListener('click', () => ajusta(() => sorteiaLook()));
  // Voltar ao sorteio: a prévia e o nome voltam ao automático (sem gravar; é preciso Salvar)
  botaoVoltar.addEventListener('click', () => {
    campoNome.value = opcoes.sorteio.nome;
    mostraNome();
    ajusta(() => ({ ...opcoes.sorteio.look }));
  });
  campoNome.addEventListener('input', () => mostraNome());

  async function salva(): Promise<void> {
    if (ocupado) return;
    if (mostraNome()) {
      campoNome.focus();
      return;
    }
    ocupado = true;
    botaoSalvar.disabled = true;
    botaoSalvar.textContent = t('ed.salvando');
    erroGeral.textContent = '';
    const persona: PersonaSettings = { ...(opcoes.castId ? { castId: opcoes.castId } : {}), name: campoNome.value.trim(), look };
    let falha: string | undefined;
    try {
      falha = await opcoes.salvar(persona);
    } catch {
      falha = t('erro.nao_deu');
    }
    if (falha) {
      ocupado = false;
      botaoSalvar.disabled = false;
      botaoSalvar.textContent = t('ed.salvar');
      erroGeral.textContent = t('ed.erro', { erro: falha });
      return;
    }
    fecha();
  }

  const formulario = el(
    'form',
    { class: 'ed-forma' },
    el(
      'header',
      { class: 'ed-cab' },
      el('h2', { id: 'editor-personagem-titulo' }, opcoes.titulo),
      botaoFechar,
    ),
    el(
      'div',
      { class: 'ed-grade' },
      el(
        'div',
        { class: 'ed-previa-col' },
        el('div', { class: 'ed-previa' }, previa),
        el('div', { class: 'ed-acoes-previa' }, botaoSortear, botaoVoltar),
      ),
      el(
        'div',
        { class: 'ed-pecas' },
        el('label', { class: 'ed-campo', for: 'ed-nome' }, el('span', {}, t('ed.nome')), campoNome),
        dicaNome,
        erroNome,
        el('p', { class: 'ed-dica' }, t('ed.peca_dica')),
        controles,
      ),
    ),
    el('footer', { class: 'ed-rodape' }, erroGeral, botaoCancelar, botaoSalvar),
  );
  formulario.addEventListener('submit', (evento) => {
    evento.preventDefault();
    void salva();
  });
  dlg.append(formulario);
  dlg.addEventListener('close', () => dlg.remove());
  document.body.append(dlg);
  atualiza();
  mostraNome();
  dlg.showModal();
}
